import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, UserPlus, Mail, CreditCard, Building2, ArrowRight } from 'lucide-react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const slideUpFromBottom = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
`;

const PopupContainer = styled.div`
  /* Desktop: centered modal */
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 92%;
  max-width: 500px;
  max-height: 88vh;
  overflow-y: auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  animation: ${slideUp} 0.3s ease-out;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

  /* Mobile: bottom sheet */
  @media (max-width: 600px) {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    max-height: 82vh;
    border-radius: 20px 20px 0 0;
    animation: ${slideUpFromBottom} 0.32s cubic-bezier(0.32, 0.72, 0, 1);
  }
`;

/* Drag handle shown only on mobile */
const DragHandle = styled.div`
  display: none;
  @media (max-width: 600px) {
    display: block;
    width: 40px;
    height: 4px;
    background: #e2e8f0;
    border-radius: 2px;
    margin: 10px auto 0;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 60%, #0ea5e9 100%);
  padding: 28px 24px 24px;
  text-align: center;
  position: relative;

  @media (max-width: 600px) {
    padding: 20px 20px 18px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: background 0.2s;
  &:hover { background: rgba(255, 255, 255, 0.35); }
`;

const HeaderIcon = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  svg { width: 28px; height: 28px; color: white; }

  @media (max-width: 600px) {
    display: none;
  }
`;

const Title = styled.h2`
  color: white;
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.3;

  @media (max-width: 600px) {
    font-size: 1.1rem;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.875rem;
  margin: 0;

  @media (max-width: 600px) {
    font-size: 0.8rem;
  }
`;

const FreeBadge = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
  margin-top: 10px;
  letter-spacing: 0.3px;
`;

const Content = styled.div`
  padding: 20px 20px 0;

  @media (max-width: 600px) {
    padding: 14px 16px 0;
  }
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }

  @media (max-width: 600px) {
    padding: 10px 0;
    gap: 12px;
  }
`;

const StepNumber = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${props => props.color || '#7c3aed'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { width: 16px; height: 16px; color: white; }

  @media (max-width: 600px) {
    width: 30px;
    height: 30px;
    svg { width: 14px; height: 14px; }
  }
`;

const StepBody = styled.div`
  flex: 1;
  padding-top: 2px;
`;

const StepTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 3px;
`;

const StepDesc = styled.div`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
`;


const CTASection = styled.div`
  padding: 16px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 600px) {
    padding: 12px 16px 6px;
  }
`;

const PrimaryBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 20px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%);
  color: white;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 10px;
  text-decoration: none;
  transition: opacity 0.2s, transform 0.15s;
  &:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    color: white;
    text-decoration: none;
  }
  svg { width: 18px; height: 18px; }
`;

const SecondaryBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 20px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  color: #475569;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #334155;
    text-decoration: none;
  }
`;


const STORAGE_KEY = 'desifirms_guide_dismissed';
const AUTO_CLOSE_DELAY = 90000; // 90 seconds

const STEPS = [
  {
    icon: UserPlus,
    color: '#7c3aed',
    title: 'Create a free account',
    desc: 'Tap Register → enter a username, email & password. Or sign up instantly with Google — no verification needed.',
  },
  {
    icon: Mail,
    color: '#0ea5e9',
    title: 'Verify your email',
    desc: "We'll send an activation link to your inbox. Click it to confirm your account. (Skip this step if you used Google.)",
  },
  {
    icon: CreditCard,
    color: '#f59e0b',
    title: 'Select a plan',
    desc: 'Choose the Free plan to get started at no cost. You can try our Premium plan too — cancel anytime and we\'ll move you back to Free.',
  },
  {
    icon: Building2,
    color: '#10b981',
    title: 'Add your business',
    desc: 'Once logged in, go to Dashboard → Add Business. Or use the "List my Business" shortcut from the welcome popup.',
  },
];

const WelcomePopup = ({ onVisibilityChange, forceShow, onForceShowHandled, isAuthenticated }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isAuthenticated) return; // already have an account — no need to show
    const isDismissed = localStorage.getItem(STORAGE_KEY);
    if (!isDismissed) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated]);

  // Allow mobile nudge tap to force-open the guide even if dismissed before
  useEffect(() => {
    if (forceShow) {
      setIsVisible(true);
      if (onForceShowHandled) onForceShowHandled();
    }
  }, [forceShow, onForceShowHandled]);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setIsVisible(false), AUTO_CLOSE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (onVisibilityChange) onVisibilityChange(isVisible);
  }, [isVisible, onVisibilityChange]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, 'true'); // always dismiss permanently — show once only
    setIsVisible(false);
  };

  const handleCTAClick = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      <Overlay onClick={handleClose} />
      <PopupContainer>
        {/* Pull handle — visible only on mobile bottom sheet */}
        <DragHandle />
        <Header>
          <CloseButton onClick={handleClose} aria-label="Close">
            <X size={16} />
          </CloseButton>
          <HeaderIcon>
            <Building2 />
          </HeaderIcon>
          <Title>How to Add Your Business</Title>
          <Subtitle>Australia's South Asian community directory</Subtitle>
          <div>
            <FreeBadge>✓ Free to join · Takes 5 minutes</FreeBadge>
          </div>
        </Header>

        <Content>
          <StepList>
            {STEPS.map((step, i) => (
              <StepItem key={i}>
                <StepNumber color={step.color}>
                  <step.icon />
                </StepNumber>
                <StepBody>
                  <StepTitle>Step {i + 1} — {step.title}</StepTitle>
                  <StepDesc>{step.desc}</StepDesc>
                </StepBody>
              </StepItem>
            ))}
          </StepList>

        </Content>

        <CTASection>
          <PrimaryBtn href="/api/register/" onClick={handleCTAClick}>
            <UserPlus size={18} />
            Register Free — Get Started
            <ArrowRight size={16} />
          </PrimaryBtn>
          <SecondaryBtn href="/api/login/" onClick={handleCTAClick}>
            Already have an account? Sign in
          </SecondaryBtn>
        </CTASection>

      </PopupContainer>
    </>
  );
};

export default WelcomePopup;
